import { Tooltip } from '@material-ui/core'
import { ArticleAttributes } from 'components/facets/ArticleAttributes'
import { useUser } from 'components/providers/UserProvider'
import { useOrderStore } from 'lib/hooks'
import { getGameCodeFromName, getPriceFormat } from 'lib/utils'
import { ReactElement, useEffect, useRef, useState } from 'react'
import {
  AutoSizer,
  CellMeasurer,
  CellMeasurerCache,
  Column,
  Table,
  TableRowRenderer,
} from 'react-virtualized'
import {
  ARTICLE_STATES,
  GAME_NAMES,
  getDisplayLocationName,
  getGameFromId,
  getPlaysetCoefficient,
  Location,
  OrderArticle,
} from 'shared'
import { PickingButton } from './PickingButton'
import { PickingListSearch } from './PickingListSearch'
import { getPickedQuantityLabel, locationBeforePick } from './PickingPresenter'

export interface PickingTableProps {
  type: string
  handlePick?: (article: OrderArticle) => void
  handleCompletePicking?: (article: OrderArticle, readonly: boolean) => void
  handleReportIssue?: (a: OrderArticle) => void
  handleBackToPicking?: (a: OrderArticle) => void
  handleIssues?: (a: OrderArticle) => void
}

export const PickingTable = ({
  type,
  handleCompletePicking,
  handleReportIssue,
  handleBackToPicking,
  handleIssues,
}: PickingTableProps): JSX.Element => {
  const { articlesToPick, articlesPicked } = useOrderStore()
  const { user } = useUser()
  const showAvailableQuantity = user.showAvailableQuantityInPickingLocations ?? false
  const [searchText, setSearchText] = useState('')
  const [matchIndices, setMatchIndices] = useState<number[]>([])
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0)
  const tableRef = useRef<any>(null)

  const getTableArticles = () => {
    if (type === 'toPick') {
      return articlesToPick
    } else {
      return articlesPicked
    }
  }

  const tableArticles = getTableArticles()

  const cache = new CellMeasurerCache({
    fixedWidth: true,
    minHeight: 40,
  })

  useEffect(() => {
    if (!searchText.trim()) {
      setMatchIndices([])
      setCurrentMatchIndex(0)
      return
    }

    const lowerSearch = searchText.toLowerCase()

    const searchTarget = getTableArticles()

    const matches = searchTarget.reduce<number[]>((acc, article, index) => {
      const matchInName =
        article.getLocalizedName().toLowerCase().includes(lowerSearch) ||
        (article.card?.name ?? '').toLowerCase().includes(lowerSearch)

      if (matchInName) acc.push(index)
      return acc
    }, [])

    setMatchIndices(matches)
    setCurrentMatchIndex(0)
  }, [searchText, articlesToPick])

  useEffect(() => {
    if (matchIndices.length > 0 && tableRef.current) {
      tableRef.current.scrollToRow(matchIndices[currentMatchIndex])
    }
  }, [matchIndices, currentMatchIndex])

  const cellRendererMeasurer = (
    parent: any,
    rowIndex: number | undefined,
    content: ReactElement
  ) => {
    return (
      <CellMeasurer cache={cache} parent={parent} columnIndex={0} rowIndex={rowIndex}>
        <div style={{ whiteSpace: 'normal', height: '50px' }}>{content}</div>
      </CellMeasurer>
    )
  }

  const rowRenderer: TableRowRenderer = ({ index, key, style, columns, className, rowData }) => {
    rowData
    const isHighlighted = matchIndices.includes(index)
    return (
      <div
        key={key}
        style={style}
        className={`${className} ${isHighlighted ? 'highlight-row' : ''}`}
      >
        {columns}
      </div>
    )
  }

  const listOfChosenLocations = (location: Location, article: OrderArticle) => {
    if (!location) {
      return (
        <span data-testid={'missing-location-' + article.ptOrderArticleId}>
          Missing location. Card not found in inventory
        </span>
      )
    } else {
      if (article.articleState === ARTICLE_STATES.PICKED) {
        return (
          <div key={location.name} className="d-flex flex-row">
            <span>{`${getDisplayLocationName(location.name)}: ${getPickedQuantityLabel(
              article
            )}`}</span>
          </div>
        )
      } else {
        return <span>{getDisplayLocationName(location.name)}</span>
      }
    }
  }
  //width: 1200 is min-width
  return (
    <>
      <PickingListSearch
        searchText={searchText}
        matchIndices={matchIndices}
        currentMatchIndex={currentMatchIndex}
        setSearchText={setSearchText}
        setCurrentMatchIndex={setCurrentMatchIndex}
      />

      <div className="responsive-table-container">
        <AutoSizer disableHeight>
          {({ width }) => (
            <Table
              ref={tableRef}
              deferredMeasurementCache={cache}
              width={Math.max(width, 1200)}
              height={500}
              headerHeight={40}
              rowHeight={70}
              rowCount={tableArticles.length}
              rowGetter={({ index }) => tableArticles[index]}
              rowRenderer={rowRenderer}
              className={`picking-table ${type}`}
            >
              <Column
                width={35}
                label=""
                dataKey=""
                disableSort={true}
                cellRenderer={({ rowData: a }) => (
                  <Tooltip title={getGameFromId(a.card.idGame!) || ''}>
                    <span
                      className={`ss-${getGameCodeFromName(
                        GAME_NAMES[a.card?.idGame ?? 0]
                      )} ss-game-icon ss-fw`}
                      style={{ fontSize: '1.5rem' }}
                    ></span>
                  </Tooltip>
                )}
              />

              <Column
                width={140}
                flexGrow={14}
                flexShrink={1}
                label="Name"
                dataKey="name"
                disableSort={true}
                headerRenderer={() => <div data-testid={type + '-header-name'}>Name</div>}
                cellRenderer={({ rowData: a, rowIndex }) => (
                  <div data-testid={type + '-localizedName-' + rowIndex}>
                    <div>{a.getLocalizedName()}</div>
                    {a.idLanguage !== 1 && <div>[{a.card.name}]</div>}
                  </div>
                )}
              />

              <Column
                width={140}
                flexGrow={14}
                flexShrink={1}
                disableSort={true}
                label="Cn - Expansion"
                dataKey="expansion"
                cellRenderer={({ rowData: a }) => (
                  <div className="d-flex flex-column" style={{ whiteSpace: 'break-spaces' }}>
                    <span>
                      {' '}
                      # {a.card.cn} - {a.card.set}
                    </span>
                    <span> {a.card.expansion} </span>
                  </div>
                )}
              />

              <Column
                width={60}
                flexGrow={6}
                flexShrink={1}
                disableSort={true}
                label="Color - Rarity"
                dataKey="rarity"
                cellRenderer={({ rowData: a }) => (
                  <div className="d-flex flex-column">
                    <span>{a.card?.colors}</span>
                    <span>{a.card?.rarityStr}</span>
                  </div>
                )}
              />

              <Column
                width={95}
                disableSort={true}
                label="Picked Q.ty"
                dataKey="quantity"
                headerClassName="centered-header"
                cellRenderer={({ rowData: a, rowIndex }) => (
                  <div
                    className="d-flex align-items-center justify-content-center"
                    data-testid={type + '-picked-quantity-' + rowIndex}
                  >
                    {a.pickedQuantity}/
                    {a.isPlayset ? a.quantity * getPlaysetCoefficient(a.card.idGame!) : a.quantity}
                  </div>
                )}
              />

              <Column
                width={60}
                flexGrow={6}
                flexShrink={1}
                disableSort={true}
                label="Price"
                dataKey="price"
                cellRenderer={({ rowData: a, rowIndex }) => (
                  <div className="text-end" data-testid={type + '-price-' + rowIndex}>
                    {getPriceFormat(a.price, a.currencyCode)}{' '}
                  </div>
                )}
              />

              <Column
                width={80}
                flexGrow={8}
                flexShrink={1}
                label="Properties"
                dataKey=""
                disableSort={true}
                cellRenderer={({ rowData: a, rowIndex }) => {
                  return (
                    <div>
                      <ArticleAttributes
                        article={a}
                        iconHeight={18}
                        identifierForDataTestId={type + '-' + rowIndex.toString() + '-'}
                      />
                    </div>
                  )
                }}
              />

              <Column
                width={100}
                flexGrow={10}
                flexShrink={1}
                disableSort={true}
                label="Comments"
                dataKey="comments"
                className="picking-comment-cell"
                cellRenderer={({ rowData: a, parent, rowIndex }) =>
                  cellRendererMeasurer(
                    parent,
                    rowIndex,
                    <span className="picking-comment-clamp-3" title={a.comment}>
                      {a.comment}
                    </span>
                  )
                }
              />

              <Column
                width={86}
                disableSort={true}
                headerClassName="centered-header"
                label="Order bin"
                dataKey="order-bin"
                headerRenderer={() => <div data-testid={type + '-header-order-bin'}>Order bin</div>}
                cellRenderer={({ rowData: a }) => (
                  <div className="d-flex justify-content-center">{a.pickingId}</div>
                )}
              />

              {type === 'toPick' && (
                <Column
                  width={100}
                  flexGrow={10}
                  flexShrink={1}
                  label="Locations"
                  dataKey="locations"
                  disableSort={true}
                  headerClassName="centered-header"
                  headerRenderer={() => <div data-testid="header-locations">Qty to pick</div>}
                  cellRenderer={({ rowData: a, parent, rowIndex }) =>
                    cellRendererMeasurer(
                      parent,
                      rowIndex,
                      <div
                        className="d-flex justify-content-center align-items-center h-100"
                        data-testid={'location-' + rowIndex}
                      >
                        <span>{locationBeforePick(a, showAvailableQuantity)}</span>
                      </div>
                    )
                  }
                />
              )}

              {type === 'toPick' && (
                <Column
                  width={100}
                  flexGrow={10}
                  flexShrink={1}
                  label=""
                  dataKey=""
                  disableSort={true}
                  cellRenderer={({ rowData: a, rowIndex }) => (
                    <div data-testid={'pick-' + rowIndex}>
                      <PickingButton
                        type="pick"
                        article={a}
                        handleCompletePicking={() => {
                          handleCompletePicking!(a, false)
                        }}
                      />
                    </div>
                  )}
                />
              )}

              {type === 'toPick' && (
                <Column
                  width={120}
                  flexGrow={12}
                  flexShrink={1}
                  label=""
                  dataKey=""
                  disableSort={true}
                  cellRenderer={({ rowData: a, rowIndex }) => (
                    <div data-testid={'reportIssue-' + rowIndex}>
                      <PickingButton
                        type="reportIssue"
                        article={a}
                        handleReportIssue={handleReportIssue}
                      />
                    </div>
                  )}
                />
              )}

              {type === 'picked' && (
                <Column
                  width={100}
                  flexGrow={10}
                  flexShrink={1}
                  label="Locations"
                  dataKey="locations"
                  disableSort={true}
                  headerClassName="centered-header"
                  cellRenderer={({ rowData: a, parent, rowIndex }) =>
                    cellRendererMeasurer(
                      parent,
                      rowIndex,
                      <div
                        className="d-flex h-100 flex-column align-items-center justify-content-center"
                        data-testid={'picked-location-' + rowIndex}
                      >
                        {listOfChosenLocations(a.locationInfo, a)}
                      </div>
                    )
                  }
                />
              )}

              {type === 'picked' && (
                <Column
                  width={100}
                  flexGrow={10}
                  flexShrink={1}
                  label=""
                  dataKey=""
                  cellRenderer={({ rowData: a, rowIndex }) => (
                    <div data-testid={'picked-' + rowIndex}>
                      {a.articleState === ARTICLE_STATES.WITH_ISSUE ? (
                        <PickingButton type="issues" article={a} handleIssues={handleIssues} />
                      ) : (
                        <PickingButton
                          type="picked"
                          article={a}
                          handleCompletePicking={() => handleCompletePicking!(a, true)}
                        />
                      )}
                    </div>
                  )}
                />
              )}

              {type === 'picked' && (
                <Column
                  width={130}
                  flexGrow={13}
                  flexShrink={1}
                  label=""
                  dataKey=""
                  cellRenderer={({ rowData: a, rowIndex }) => (
                    <div data-testid={'backToPicking-' + rowIndex}>
                      <PickingButton
                        type="backToPicking"
                        article={a}
                        handleBackToPicking={handleBackToPicking}
                      />
                    </div>
                  )}
                />
              )}
            </Table>
          )}
        </AutoSizer>
      </div>
    </>
  )
}
